'use client';

import { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { SensorsIcon, ManageSearchIcon, UndoIcon } from '@/shared/ui/display/EmojiIcon';
import { SimpleSearch } from '@/shared/ui/search/SimpleSearch';
import { AdvancedSearch } from '@/shared/ui/search/AdvancedSearch';
import { orderSimpleSearchConfig, orderAdvancedSearchConfig } from '@/entities/order';
import type { SimpleSearchQuery } from '@/shared/ui/search/SimpleSearch/types/search.types';
import type { SearchQuery } from '@/shared/ui/search/AdvancedSearch/types/query.types';
import { OrderSyncIconButton } from './OrderSyncTrigger';

interface OrderTableSearchProps {
  mode: 'simple' | 'advanced';
  onModeChange: (mode: 'simple' | 'advanced') => void;
  onSearch: (query: SimpleSearchQuery | SearchQuery) => void;
  initialSimpleQuery?: SimpleSearchQuery;
  initialAdvancedQuery?: SearchQuery;
  /** 실시간 주문조회 모드 (버튼 클릭 시점 이후 접수된 주문만 5초 간격 폴링) */
  isRealtimeActive?: boolean;
  onRealtimeToggle?: () => void;
}

/**
 * 주문 테이블 검색 영역
 * SimpleSearch와 AdvancedSearch를 전환할 수 있는 컴포넌트
 */
export function OrderTableSearch({
  mode,
  onModeChange,
  onSearch,
  initialSimpleQuery,
  initialAdvancedQuery,
  isRealtimeActive = false,
  onRealtimeToggle,
}: OrderTableSearchProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const isCompact = width > 0 && width < 600;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSimpleSearch = (query: SimpleSearchQuery) => {
    onSearch(query);
  };

  const handleAdvancedSearch = (query: SearchQuery) => {
    onSearch(query);
  };

  const handleToggleMode = () => {
    onModeChange(mode === 'simple' ? 'advanced' : 'simple');
    setAnchorEl(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box ref={containerRef} sx={{ mb: 2 }}>
      {mode === 'simple' ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: 1 }}>
          <Box sx={{ flex: 1 }}>
            <SimpleSearch
              config={orderSimpleSearchConfig}
              onSearch={handleSimpleSearch}
              initialQuery={initialSimpleQuery}
              endAdornment={
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Tooltip title="고급검색">
                    <IconButton
                      onClick={handleToggleMode}
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          color: 'text.primary',
                        },
                      }}
                      aria-label="고급 검색 모드 전환"
                    >
                      <ManageSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      isRealtimeActive
                        ? '실시간 조회 중지'
                        : '실시간 주문조회 (클릭 시점 이후 접수 주문, 5초 간격)'
                    }
                  >
                    <IconButton
                      onClick={onRealtimeToggle}
                      size="small"
                      color={isRealtimeActive ? 'primary' : 'default'}
                      sx={{
                        color: isRealtimeActive ? 'primary.main' : 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          color: isRealtimeActive ? 'primary.dark' : 'text.primary',
                        },
                      }}
                      aria-label={isRealtimeActive ? '실시간 조회 중지' : '실시간 주문조회'}
                    >
                      <SensorsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <OrderSyncIconButton />
                </Box>
              }
            />
          </Box>
        </Box>
      ) : (
        <AdvancedSearch
          config={orderAdvancedSearchConfig}
          onSearch={handleAdvancedSearch}
          initialQuery={initialAdvancedQuery}
          endAdornment={
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Tooltip title="간단검색">
                <IconButton
                  onClick={handleToggleMode}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'text.primary',
                    },
                  }}
                  aria-label="간단 검색 모드 전환"
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  isRealtimeActive
                    ? '실시간 조회 중지'
                    : '실시간 주문조회 (클릭 시점 이후 접수 주문, 5초 간격)'
                }
              >
                <IconButton
                  onClick={onRealtimeToggle}
                  size="small"
                  color={isRealtimeActive ? 'primary' : 'default'}
                  sx={{
                    color: isRealtimeActive ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: isRealtimeActive ? 'primary.dark' : 'text.primary',
                    },
                  }}
                  aria-label={isRealtimeActive ? '실시간 조회 중지' : '실시간 주문조회'}
                >
                  <SensorsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <OrderSyncIconButton />
            </Box>
          }
        />
      )}
    </Box>
  );
}
